import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AddIconComponent } from '@kuiper/icons';
import { UserService } from '../../users-database/user.service';
import { Router } from '@angular/router';
import { RemoveSuperManagerComponent } from '../remove-super-manager/remove-super-manager.component';
import { BadgeComponent } from '../../../../../../ui-components/lib/badge/badge.component';
import { PaginatorComponent } from '../../../../../../ui-components/lib/paginator/paginator.component';
import { AssignGroupUsersComponent } from '../../../../../common/components/assign-group-users/assign-group-users.component';
import { OverlayLoaderComponent } from '../../../../../common/components/overlay-loader/overlay-loader.component';
import { GroupUserCardComponent } from '../../../../../common/components/group-user-card/group-user-card.component';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../../common/constants/pagination.constants';
import { ModalServiceService } from '../../../../../../ui-components/lib/modal/modal-service.service';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags/feature-flags.service';
import { RoleTypes } from '../../../../../common/constants/role-types.constant';
import { TenantService } from '../../../../../common/services/tenant/tenant.service';
import { ToasterService } from '../../../../../../ui-components/services/toast/toast.service';
import { ActionList } from '../../../../../common/models/action-list.model';
import { ADMIN_ROUTES } from '../../../../../common/constants/admin.routing.constants';
import { Option } from '../../../../../common/models/option.model';
import {
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '../../../../../common/helpers/system-tabs-layout/system-tabs-layout';

@Component({
  selector: 'lib-super-manager-list',
  imports: [
    BadgeComponent,
    GroupUserCardComponent,
    PaginatorComponent,
    TranslocoPipe,
    OverlayLoaderComponent,
    RemoveSuperManagerComponent,
    AddIconComponent,
    AssignGroupUsersComponent,
  ],
  providers: [provideSystemTabsLayout()],
  templateUrl: './super-manager-list.component.html',
  styleUrl: './super-manager-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SuperManagerListComponent implements OnInit {
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();
  readonly pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;

  modalService = inject(ModalServiceService);
  transloco = inject(TranslocoService);
  userService = inject(UserService);
  featureFlagsService = inject(FeatureFlagsService);
  toastService = inject(ToasterService);
  router = inject(Router);
  tenantService = inject(TenantService);

  roleTypes = RoleTypes;
  removeSuperManagerModal =
    viewChild<TemplateRef<unknown>>('removeSuperManager');
  assignSuperManagersDialog = viewChild<TemplateRef<unknown>>(
    'assignSuperManagersDialog'
  );

  adminUsersCards = signal<Option[]>([]);
  selectedUsers = signal<Option[]>([]);
  selectedUsersIds = signal<string[]>([]);
  currentPage = signal<number>(1);
  currentSize = signal<number>(15);
  totalPages = signal<number>(20);
  isLoading = signal<boolean>(false);
  totalManagersCount = signal<string>('');
  selectedUserId = signal<string>('');
  isRemoveSuperManagerLoading = signal<boolean>(false);
  isLoadingGroupUsers = signal<boolean>(false);
  isChangeAdminRoleLoading = signal<boolean>(false);
  modalCurrentPage = signal<number>(1);
  modalTotalPages = signal<number>(20);
  admins = signal<Option[]>([]);
  lastItemInPage = signal<boolean>(false);

  actionList: ActionList[] = [
    {
      key: 'view_details',
      hasIcon: true,
      label: this.transloco.translate('view_details_action_item'),
      hasDivider: false,
      labelCustomStyle: '',
      hasToggle: false,
      toggleValue: false,
    },
    {
      key: 'remove',
      hasIcon: true,
      label: this.transloco.translate('remove_action_item'),
      hasDivider: false,
      labelCustomStyle: 'text-destructive-500 font-medium text-xs',
      hasToggle: false,
      toggleValue: false,
    },
  ];

  ngOnInit() {
    this.getSuperManagersList();
  }

  handleJobTitleUsers(res: any) {
    this.admins.set(
      res.map((user: any) => {
        return {
          label: user.name,
          value: user.id.toString(),
          image: user.image,
          email: user.email,
        };
      })
    );
  }

  actionListItemClick(event: any) {
    if (event.action == 'remove') {
      this.selectedUserId.set(event.item.value);
      this.openModal();
    } else {
      this.router.navigateByUrl(
        this.tenantService.adminPath(
          `${ADMIN_ROUTES.USERS_DATABASE.DETAILS}/${event.item.value}`
        )
      );
    }
  }

  handlePagination($event: number) {
    this.currentPage.set($event);
    this.getSuperManagersList();
  }

  getUsers(name = '', page = 1) {
    this.modalCurrentPage.set(page);
    this.isLoadingGroupUsers.set(true);
    this.userService
      .userLookup({
        name,
        page: this.modalCurrentPage(),
        filtersNotState: 'inactive',
        filtersNotRole: this.roleTypes.SUPER_MANAGER,
        filtersNotStateCancelled: 'cancelled',
      })
      .subscribe(response => this.handleUsersResponse(response));
  }

  handleUsersResponse(response: any) {
    const mappedData = response.data.map((user: any) => ({
      ...user,
      isSelected: this.selectedUsers().some(
        selectedUser => selectedUser.value === user.value
      ),
    }));

    if (this.modalCurrentPage() === 1) {
      this.adminUsersCards.set(mappedData);
    } else {
      this.adminUsersCards().push(...mappedData);
    }
    this.modalTotalPages.set(response.meta.last_page);
    this.isLoadingGroupUsers.set(false);
  }

  openModal() {
    const modal = {
      isOpen: true,
      hasCloseIcon: false,
      customizeContent: this.removeSuperManagerModal(),
      width: '24',
    };

    this.modalService.modalContent.set(modal);
  }

  closeSuperManagerModal() {
    this.modalService.modalContent.set({ isOpen: false });
  }

  goBack() {
    history.back();
  }

  openAssignSuperManagersDialog() {
    this.clearSelectedUsers();
    this.modalCurrentPage.set(1);
    this.getUsers();

    this.modalService.modalContent.set({
      isOpen: true,
      customizeContent: this.assignSuperManagersDialog(),
      canNotCloseFromOutside: true,
      width: '50.5',
    });
  }

  clearSelectedUsers() {
    this.selectedUsers.set([]);
    this.selectedUsersIds.set([]);
  }

  onSelectAdminUser(user: Option) {
    user.isSelected = true;
    this.selectedUsers().unshift(user);

    this.adminUsersCards.set(
      this.adminUsersCards().filter(card => card.value !== user.value)
    );

    if (
      this.modalCurrentPage() < this.modalTotalPages() &&
      this.adminUsersCards().length < 7
    ) {
      this.getUsers('', this.modalCurrentPage() + 1);
    }
  }

  removeSelectedAdminUser(user: Option) {
    user.isSelected = false;
    this.selectedUsers.set(
      this.selectedUsers().filter(card => card.value !== user.value)
    );

    this.adminUsersCards().unshift(user);
  }

  updateSelectedUserIds() {
    this.selectedUsersIds().push(
      ...this.selectedUsers().map(user => user.value)
    );
  }

  changeAdminRole() {
    this.isChangeAdminRoleLoading.set(true);
    this.updateSelectedUserIds();

    this.userService
      .assignUserRole({
        role_name: this.roleTypes.SUPER_MANAGER,
        user_ids: this.selectedUsersIds(),
      })
      .subscribe({
        next: () => {
          this.getSuperManagersList();
          this.clearSelectedUsers();
        },
        error: (error: any) => {
          this.isChangeAdminRoleLoading.set(false);
          this.toastService.add(
            error.message,
            '',
            'assets/images/icons/error.svg',
            'error'
          );
        },
        complete: () => {
          this.isChangeAdminRoleLoading.set(false);
          this.closeSuperManagerModal();
        },
      });
  }

  onRemoveSuperManager() {
    this.isRemoveSuperManagerLoading.set(true);
    this.userService
      .removeRoleFromUser(this.selectedUserId(), this.roleTypes.SUPER_MANAGER)
      .subscribe({
        next: () => {
          this.handleRemoveSuperManagerResponse();
        },
        error: (error: any) => {
          this.isRemoveSuperManagerLoading.set(false);
          this.closeSuperManagerModal();
          this.toastService.add(
            error.error.message,
            '',
            'assets/images/icons/error.svg',
            'error'
          );
        },
      });
  }

  handleRemoveSuperManagerResponse() {
    if (this.lastItemInPage() && this.currentPage() > 1) {
      this.currentPage.set(this.currentPage() - 1);
    }
    this.getSuperManagersList();
    this.closeSuperManagerModal();
    this.isRemoveSuperManagerLoading.set(false);
  }

  getSuperManagersList() {
    this.isLoading.set(true);
    this.userService
      .getUserByRoleList(
        this.currentPage(),
        this.currentSize(),
        this.roleTypes.SUPER_MANAGER
      )
      .subscribe({
        next: (res: any) => this.handleSuperManagersListResponse(res),
        error: (error: any) => {
          this.isLoading.set(false);
          this.toastService.add(
            error?.error?.message || error?.message,
            '',
            'assets/images/icons/error.svg',
            'error'
          );
        },
        complete: () => {
          this.handleJobTitleUsers(this.admins());
          this.isLoading.set(false);
        },
      });
  }

  handleSuperManagersListResponse(response: any) {
    this.admins.set(response.data);
    this.totalManagersCount.set(response.meta.total.toString());
    this.totalPages.set(response.meta.last_page);
    this.lastItemInPage.set(response.meta.from - response.meta.to == 0);
  }
}
